import TextEdit from '../../../molecules/TextEdit';
import TaxIdInput from '../../../molecules/TaxIdInput';
import SelectTextField from '../../../molecules/SelectTextField';
import { formatCurrency } from '../../../../utils/collateralFormatters';
import OperationSelectList from './OperationSelectList';
import useCreateAnalysisPageForm from '../../../../hooks/useCreateAnalysisPageForm';

const LoadingOverlay = ({ message }: { message: string }) => (
    <div className="absolute inset-0 bg-white/70 dark:bg-slate-800/70 z-10 flex items-center justify-center rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <svg className="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {message}
        </div>
    </div>
);

interface Props {
    onClose: () => void;
    onCreated: () => void;
}

const NewAnalysisFromValidation = ({ onClose, onCreated }: Props) => {
    const {
        step, setStep, selected, search, setSearch, statusFilter, setStatusFilter,
        operationCode, setOperationCode, assignorName, setAssignorName,
        assignorTaxId, setAssignorTaxId, operationType, setOperationType,
        obligationType, setObligationType,
        personResults, isSearchingPerson, showPersonDropdown,
        isLoading, isCreating, filteredOps,
        handleSelectOperation, searchPerson, handleSelectPerson, handleCreate,
    } = useCreateAnalysisPageForm({ onCreated });

    if (step === 'select') {
        return (
            <div className="flex flex-col h-full relative">
                {isLoading && <LoadingOverlay message="Carregando operações..." />}
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Nova Análise de Validação</h3>
                    <div className="flex items-center gap-3">
                        <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-sm">Cancelar</button>
                        <button onClick={() => selected && setStep('configure')} disabled={!selected}
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">Próximo</button>
                    </div>
                </div>

                <OperationSelectList
                    operations={filteredOps}
                    isLoading={isLoading}
                    selected={selected}
                    onSelect={handleSelectOperation}
                    showStatusFilter
                />
            </div>
        );
    }

    return (
        <div className="relative">
            {isCreating && <LoadingOverlay message="Aguarde, criando análise..." />}
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Configurar Análise</h3>
                <div className="flex items-center gap-3">
                    <button onClick={() => setStep('select')} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 dark:bg-slate-700 dark:text-gray-300 text-sm">Voltar</button>
                    <button onClick={handleCreate} disabled={isCreating || !assignorName || !assignorTaxId}
                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">
                        {isCreating ? 'Criando...' : 'Criar Análise'}
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 text-sm">
                    <div className="text-gray-500 dark:text-gray-400">{selected!.operationCode} — {formatCurrency(selected!.totalAmount || 0)} · {selected!.totalReceivables || 0} títulos</div>
                </div>

                <TextEdit label="Código do lote" value={operationCode} onChange={e => setOperationCode(e.target.value)} />

                <div className="relative">
                    <TextEdit label="Nome do cedente (Enter para buscar)" value={assignorName} maxLength={180}
                        onChange={e => setAssignorName(e.target.value)}
                        onKeyDown={(e: any) => e.key === 'Enter' && searchPerson(assignorName)} />
                    {isSearchingPerson && <div className="text-xs text-gray-400 mt-1">Buscando...</div>}
                    {showPersonDropdown && personResults.length > 0 && (
                        <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg max-h-48 overflow-auto">
                            {personResults.slice(0, 6).map((p: any) => (
                                    <div key={p.personId || p.uuid} onClick={() => handleSelectPerson(p)}
                                        className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer text-sm">
                                        <span className="font-medium dark:text-white">{p.legalName || p.name}</span>
                                        {p.govTaxIdFormatted && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">{p.govTaxIdFormatted}</span>}
                                    </div>
                            ))}
                        </div>
                    )}
                </div>

                <TaxIdInput type="PJ" label="CNPJ do cedente" value={assignorTaxId}
                    onChange={setAssignorTaxId} />

                <div className="grid grid-cols-2 gap-3">
                    <SelectTextField
                        label="Tipo Operação"
                        value={operationType}
                        onChange={e => setOperationType(e.target.value)}
                        items={[
                            { value: 'CESSAO', label: 'Cessão' },
                            { value: 'CPRF', label: 'CPRF' },
                        ]}
                        emptyLabel="Selecione..."
                    />
                    <SelectTextField
                        label="Obrigação"
                        value={obligationType}
                        onChange={e => setObligationType(e.target.value)}
                        items={[
                            { value: 'WITHOUT_RECOURSE', label: 'Sem Regresso' },
                            { value: 'WITH_RECOURSE', label: 'Com Regresso' },
                            { value: 'NO_OBLIGATION', label: 'Sem Garantia' },
                        ]}
                        emptyLabel="Selecione..."
                    />
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <TextEdit label="Total recebíveis" value={String(selected!.totalReceivables || 0)} disabled />
                    <TextEdit label="Valor total" value={formatCurrency(selected!.totalAmount || 0)} disabled />
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-300">
                    Validação de contato (email/telefone) será habilitada automaticamente. Após criar, carregue os recebíveis pela listagem.
                </div>
            </div>
        </div>
    );
};

export default NewAnalysisFromValidation;
